<template>
  <page layout="">
    <el-dialog
      :before-close="handleTypeDialogClose"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
      :visible="typeDialogVisible"
      width="850px"
    >
      <div slot="title">
        <div class="el-dialog__title">
          {{ $t('ApplicationList_Popup_Add_Title') }}
        </div>
      </div>
      <grid
        :active-type="activeType"
        :items="items"
        @typeChanged="newType => (activeType = newType)"
      />
      <span slot="footer" class="dialog-footer">
        <el-button plain type="primary" @click="handleTypeDialogClose">
          <span>{{ $t('Button_Cancel') }}</span>
        </el-button>
        <el-button
          :disabled="activeType === null"
          type="primary"
          @click="handleNext"
        >
          <span>{{ $t('ApplicationList_Button_Next') }}</span>
          <i class="el-icon-arrow-right el-icon--right" />
        </el-button>
      </span>
    </el-dialog>
    <el-dialog
      :before-close="handleFormDialogClose"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
      :top="popupTopMargin"
      :visible="formDialogVisible"
      class="form-dialog"
      width="650px"
    >
      <div slot="title">
        <div class="el-dialog__title">
          <div v-if="activeType === ApplicationTypes.ios">
            {{ $t('ApplicationList_Popup_Add_IOS_Title') }}
          </div>
          <div v-if="activeType === ApplicationTypes.android">
            {{ $t('ApplicationList_Popup_Add_Android_Title') }}
          </div>
          <div v-if="activeType === ApplicationTypes.web">
            {{ $t('ApplicationList_Popup_Add_Website_Title') }}
          </div>
        </div>
      </div>
      <el-container>
        <el-aside v-if="hasSubType()" class="subTypeSide" width="25%">
          <el-row>
            <el-col :span="24">
              <el-menu background-color="#f8fafc" default-active="0">
                <el-menu-item
                  v-for="(subType, index) in subTypes[activeType]"
                  :key="index"
                  @click="activeSubType = subType.type"
                >
                  <el-image
                    :src="subType.icon"
                    style="height: 25px; width: 25px"
                  ></el-image>
                  <span>{{ $t(subType.title) }}</span>
                </el-menu-item>
              </el-menu>
            </el-col>
          </el-row>
        </el-aside>
        <el-main>
          <app-form
            v-if="activeType"
            ref="app-form"
            :sub-type="activeSubType"
            :type="activeType"
            @keyup.native="change"
          />
        </el-main>
      </el-container>
      <span slot="footer" class="dialog-footer">
        <el-button plain type="primary" @click="handleFormDialogClose">
          <span>{{ $t('ApplicationList_Button_Back') }}</span>
        </el-button>
        <el-button :disabled="!isFormValid" type="primary" @click="save">
          <i class="el-icon-check el-icon--left" />
          <span v-if="activeType === 'ios' || activeType === 'android'">{{
            $t('ApplicationList_Button_Connect_App')
          }}</span>
          <span v-if="activeType === 'web'">{{
            $t('ApplicationList_Button_Connect_Website')
          }}</span>
        </el-button>
      </span>
    </el-dialog>
    <page-header-new :title="pageTitle">
      <template v-slot:actions>
        <el-button
          class="btn-connect btn-custom-icon"
          type="primary"
          @click="typeDialogVisible = true"
        >
          <icon class="icon" glyph="app-lightning" style="margin-right: 12px" />
          {{ $t('ApplicationList_Button_Connect') }}
        </el-button>
      </template>
    </page-header-new>
    <page-content>
      <application-list-no-data v-if="!isAppAvailable">
        <template v-slot:actions>
          <el-button
            class="btn-connect btn-custom-icon"
            type="default"
            @click="typeDialogVisible = true"
          >
            <icon
              class="icon"
              glyph="app-lightning"
              style="margin-right: 12px"
            />
            {{ $t('ApplicationList_Button_Connect') }}
          </el-button>
        </template>
      </application-list-no-data>
      <application-list v-else />
    </page-content>
  </page>
</template>

<script>
import { projectEndpoint } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import { ApplicationTypes } from '@/modules/account/enum/ApplicationEnum'
import Page from '@/common/components/page/Page'
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import Grid from '@/common/components/layout/Grid'
import AppForm from '@/modules/account/components/AppForm'
import { EventBus } from '@/core/EventBus'
import store from '@/core/store'
import PageContent from '@/common/components/page/PageContent'
import ApplicationListNoData from '@/modules/account/components/ApplicationListNoData'
import ApplicationList from '@/modules/account/components/ApplicationList'

export default {
  name: 'ApplicationListPage',

  components: {
    PageContent,
    AppForm,
    PageHeaderNew,
    Page,
    ApplicationListNoData,
    ApplicationList,
    Grid
  },

  data() {
    return {
      types: {},
      activeType: null,
      activeSubType: null,
      ApplicationTypes,
      typeDialogVisible: false,
      formDialogVisible: false,
      isAppAvailable: false,
      isFormValid: false
    }
  },

  created() {
    RestApi.get(this.optionsEndpoint).then(({ data }) => {
      this.types = data.types
    })
  },

  mounted() {
    if (store.getters.applications.length > 0) {
      this.isAppAvailable = true
    }
    EventBus.$on('closeAppDialogs', () => {
      this.handleTypeDialogClose()
      this.handleFormDialogClose()
    })
  },
  destroyed() {
    EventBus.$off('closeAppDialogs')
  },

  computed: {
    applications() {
      return store.getters.applications
    },
    items() {
      return [
        {
          type: 'android',
          title: 'ApplicationList_Connect_Android',
          icon: 'smartphone'
        },
        {
          type: 'ios',
          title: 'ApplicationList_Connect_iOS',
          icon: 'apple'
        },
        {
          type: 'web',
          title: 'ApplicationList_Connect_Web',
          icon: 'web'
        }
      ]
    },
    subTypes() {
      return {
        android: [
          {
            type: 'android',
            title: 'ApplicationList_Connect_Android_SubType_Android',
            icon: '/img/icon/android.svg'
          },
          {
            type: 'huawei',
            title: 'ApplicationList_Connect_Android_SubType_Huawei',
            icon: '/img/icon/huawei.png'
          }
        ]
      }
    },
    pageTitle() {
      return this.$t(
        this.applicationsCount
          ? this.$route.meta.title
          : 'ApplicationList_Title_NoData'
      )
    },
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: 'applications',
        params: {}
      })
    },
    readonly() {
      return !this.$can('updateProjectItem')
    },
    optionsEndpoint() {
      return projectEndpoint({ url: 'applications/options' }).url
    },
    applicationsCount() {
      return this.$store.getters.applications.length
    },
    popupTopMargin() {
      if (
        window.innerHeight > 700 ||
        this.activeType !== ApplicationTypes.web
      ) {
        return '15vh'
      }

      return '5vh'
    }
  },

  methods: {
    change() {
      this.$refs['app-form'].$refs['form']
        .validate()
        .then(res => {
          if (res === true) {
            this.isFormValid = true
          }
        })
        .catch(() => {
          this.isFormValid = false
        })
    },
    save() {
      this.$refs['app-form'].$refs['form'].save()
    },
    handleTypeDialogClose() {
      this.typeDialogVisible = false
      this.isFormValid = false
      this.activeType = null
      this.activeSubType = null
    },
    handleFormDialogClose() {
      this.$refs['app-form'].resetForm()
      this.isFormValid = false
      this.formDialogVisible = false
    },
    handleNext() {
      this.formDialogVisible = true
    },
    hasSubType() {
      return (
        this.activeType !== null &&
        Object.keys(this.subTypes).indexOf(this.activeType) >= 0
      )
    }
  }
}
</script>

<style scoped>
.multi-button-container {
  display: flex;
  justify-content: center;
}

.multi-button-spacing {
  margin: 0 10px;
}

.sdkOptions {
  display: flex;
}

.sdkOptions a {
  margin-left: 20px;
}

.empty-title {
  font-size: 20px;
  font-weight: 600;
  color: #5c6374;
}

.empty-title-2 {
  font-size: 16px;
  font-weight: 400;
  color: #7e8596;
}

.empty-layout {
  text-align: center;
  line-height: 32px;
  padding: 10% 0 10% 0;
}

.connect-row {
  padding-top: 25px;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
}

>>> .el-dialog__footer {
  border-top: 1px solid #ebeef5;
}

>>> .el-dialog__header {
  border-bottom: 1px solid #ebeef5;
}

>>> .form-dialog .el-dialog__body {
  max-height: 65vh;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0;
}

.subTypeSide {
  background: #f8fafc;
}
</style>



// WEBPACK FOOTER //
// src/modules/account/pages/ApplicationListPage.vue